import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';

// Icons
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';

// Context
import AuthContext from '../../context/AuthContext';

// Services
import { 
  getTechnicianInventory, 
  updateTechnicianInventoryStatus,
  getStatusDisplayText,
  getStatusColor,
  getAvailableStatusTransitions,
  canReturnInventory,
  formatDate
} from '../../services/technicianInventoryService';
import { getTechnicians } from '../../services/technicianService';

// Components
import ReturnInventoryDialog from '../../components/ReturnInventoryDialog';

const TechnicianInventory = () => {
  const { user } = useContext(AuthContext);
  const [inventory, setInventory] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [technicianFilter, setTechnicianFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialogs
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  // Check if user can manage inventory
  const canManage = user && ['manager', 'purchaser', 'warehouse_staff'].includes(user.role);

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      
      const filters = {};
      if (technicianFilter) filters.technician_id = technicianFilter;
      if (statusFilter) filters.status = statusFilter;
      
      const [inventoryData, techniciansData] = await Promise.all([
        getTechnicianInventory(filters),
        getTechnicians({ include_inactive: false })
      ]);
      
      setInventory(inventoryData);
      setTechnicians(techniciansData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load technician inventory. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [technicianFilter, statusFilter]);

  // Handle status update
  const handleStatusUpdate = async () => {
    try {
      await updateTechnicianInventoryStatus(selectedRecord.id, {
        status: newStatus,
        notes: statusNotes
      });
      
      setStatusDialogOpen(false);
      setSelectedRecord(null);
      setNewStatus('');
      setStatusNotes('');
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.message || 'Failed to update status');
    }
  };

  // Handle dialogs
  const handleOpenReturnDialog = (record) => {
    setSelectedRecord(record);
    setReturnDialogOpen(true);
  };

  const handleCloseReturnDialog = () => {
    setReturnDialogOpen(false);
    setSelectedRecord(null);
    fetchData();
  };

  const handleOpenStatusDialog = (record) => {
    setSelectedRecord(record);
    setNewStatus(record.status);
    setStatusNotes('');
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelectedRecord(null);
    setNewStatus('');
    setStatusNotes('');
  };

  // Get summary statistics
  const getSummaryStats = () => {
    const stats = {
      total: inventory.length,
      assigned: 0,
      on_truck: 0,
      used: 0,
      need_to_return: 0,
      returned: 0
    };

    inventory.forEach(record => {
      stats[record.status] = (stats[record.status] || 0) + 1;
    });

    return stats;
  };

  const stats = getSummaryStats();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <InventoryIcon sx={{ mr: 2, color: 'primary.main' }} />
          Technician Inventory
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Toggle Filters">
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Records
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Assigned
              </Typography>
              <Typography variant="h4" color="info.main">
                {stats.assigned}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                On Truck
              </Typography>
              <Typography variant="h4" color="primary.main">
                {stats.on_truck}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Used
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.used}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Need Return
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.need_to_return}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Returned
              </Typography>
              <Typography variant="h4" color="text.secondary">
                {stats.returned}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      {showFilters && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Technician</InputLabel>
              <Select
                value={technicianFilter}
                label="Technician"
                onChange={(e) => setTechnicianFilter(e.target.value)}
              >
                <MenuItem value="">All Technicians</MenuItem>
                {technicians.map((tech) => (
                  <MenuItem key={tech.id} value={tech.id}>
                    {tech.name} ({tech.type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="on_truck">On Truck</MenuItem>
                <MenuItem value="used">Used</MenuItem>
                <MenuItem value="need_to_return">Need to Return</MenuItem>
                <MenuItem value="returned">Returned</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Inventory Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Technician</TableCell>
                <TableCell>Part</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Locations</TableCell>
                <TableCell>First Issued</TableCell>
                <TableCell>Last Returned</TableCell>
                {canManage && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading inventory...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : inventory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1">
                      No inventory records found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                inventory.map((record) => (
                  <TableRow key={record.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {record.technician?.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.technician?.type}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {record.part?.internal_part_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.part?.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {record.total_quantity}
                        </Typography>
                        {record.record_count > 1 && (
                          <Typography variant="caption" color="text.secondary">
                            ({record.record_count} records)
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusDisplayText(record.status)}
                        color={getStatusColor(record.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        Multiple locations
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(record.first_issued_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(record.last_returned_at)}
                      </Typography>
                    </TableCell>
                    {canManage && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => window.location.href = `/technicians/${record.technician_id}/inventory`}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Return Inventory Dialog */}
      {selectedRecord && (
        <ReturnInventoryDialog
          open={returnDialogOpen}
          onClose={handleCloseReturnDialog}
          record={selectedRecord}
        />
      )}

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                label="Status"
                onChange={(e) => setNewStatus(e.target.value)}
              >
                {selectedRecord && getAvailableStatusTransitions(selectedRecord.status).map((status) => (
                  <MenuItem key={status} value={status}>
                    {getStatusDisplayText(status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancel</Button>
          <Button
            onClick={handleStatusUpdate}
            variant="contained"
            disabled={!newStatus}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TechnicianInventory;
